import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  Eye,
  Copy as CopyIcon,
  ArrowRight,
  PenLine,
  ListChecks,
  Layers,
  FileText,
  Presentation,
  Languages,
  HelpCircle,
  ClipboardList,
  Globe,
  Wand2,
  Loader2,
} from "lucide-react";
import Markdown from "@/components/Markdown";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Skeleton } from "@/components/Skeleton";
import { subjectThemeClass } from "@/lib/subject-theme";
import OGTags from "@/components/OGTags";

const ARTIFACT_META = {
  lesson_plan:  { icon: PenLine,      label: "Lesson plan" },
  quiz:         { icon: ListChecks,   label: "Quiz" },
  worksheet:    { icon: FileText,     label: "Worksheet" },
  flashcards:   { icon: Layers,       label: "Flashcards" },
  homework:     { icon: ClipboardList,label: "Homework" },
  ppt_outline:  { icon: Presentation, label: "Presentation" },
  simplified:   { icon: HelpCircle,   label: "Simplified" },
  translated:   { icon: Languages,    label: "Translation" },
};

export default function PublicLesson() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [tab, setTab] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/lessons/${shareToken}`);
        setLesson(data);
        const firstArt = Object.keys(data.artifacts || {})[0];
        setTab(firstArt || null);
      } catch {
        setNotFound(true);
      }
    })();
  }, [shareToken]);

  const duplicate = async () => {
    if (!user) {
      // Send to signup with intent
      navigate(`/signup?duplicate=${shareToken}`);
      return;
    }
    setDuplicating(true);
    try {
      const { data } = await api.post(`/public/lessons/${shareToken}/duplicate`);
      toast.success("Lesson saved to your workspace");
      navigate(`/lesson/${data.lesson_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not duplicate");
    } finally {
      setDuplicating(false);
    }
  };

  const remix = async () => {
    if (!user) {
      navigate(`/signup?remix=${shareToken}`);
      return;
    }
    setDuplicating(true);
    try {
      const { data } = await api.post(`/remix`, { share_token: shareToken, save_lesson: true });
      toast.success(`Remixed in ${Math.round(data.duration_ms / 1000)}s — ${data.coins_spent} coins used`);
      if (data.new_lesson_id) navigate(`/lesson/${data.new_lesson_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_COINS") {
        toast.error(`Need ${detail.required} coins · You have ${detail.balance}`, { description: "Tap to recharge.", action: { label: "Recharge", onClick: () => navigate("/wallet") } });
      } else {
        toast.error(typeof detail === "string" ? detail : "Remix failed");
      }
    } finally {
      setDuplicating(false);
    }
  };

  if (notFound) {
    return (
      <PublicShell>
        <div className="tb-card p-10 text-center max-w-md mx-auto">
          <span className="mx-auto h-14 w-14 grid place-items-center rounded-full border border-dashed border-border mb-3">
            <Globe className="h-6 w-6 text-muted-foreground" />
          </span>
          <p className="font-display text-lg font-medium">Lesson not found</p>
          <p className="mt-1 text-sm text-muted-foreground">This lesson may have been unpublished by its author.</p>
          <Button className="mt-4 rounded-full" onClick={() => navigate("/")}>Go to TeeBee</Button>
        </div>
      </PublicShell>
    );
  }

  if (!lesson) {
    return (
      <PublicShell>
        <div className="space-y-3 max-w-4xl mx-auto">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-80 w-full mt-6" />
        </div>
      </PublicShell>
    );
  }

  const artifactKeys = Object.keys(lesson.artifacts || {});

  return (
    <PublicShell>
      <OGTags
        title={lesson.title}
        description={`${[lesson.subject, lesson.grade && `Grade ${lesson.grade}`, lesson.curriculum].filter(Boolean).join(" · ")} · ${lesson.author_name || "TeeBee educator"}`}
        image={`${window.location.origin}/api/og/lesson/${shareToken}`}
        type="article"
      />
      {/* Hero band */}
      <section className="relative max-w-4xl mx-auto mb-6">
        <div className="absolute inset-0 tb-grid-bg opacity-50 pointer-events-none rounded-3xl" />
        <div className="relative">
          <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
            <Globe className="h-3 w-3" /> Shared via TeeBee
          </span>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium leading-tight">{lesson.title}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {[lesson.subject, lesson.grade && `Grade ${lesson.grade}`, lesson.curriculum, lesson.language].filter(Boolean).join(" · ")}
            {lesson.author_name && <> · by <span className="text-foreground font-medium">{lesson.author_name}</span></>}
            {lesson.institution_name && <span className="text-muted-foreground"> · {lesson.institution_name}</span>}
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {lesson.view_count} views</span>
            <span className="inline-flex items-center gap-1"><CopyIcon className="h-3 w-3" /> {lesson.duplicate_count} duplicates</span>
            <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> {artifactKeys.length} artifacts</span>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <Button size="lg" className="rounded-full h-12" onClick={remix} disabled={duplicating} data-testid="public-remix">
              {duplicating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />}
              Remix for my class
            </Button>
            <Button size="lg" variant="outline" className="rounded-full h-12" onClick={duplicate} disabled={duplicating} data-testid="public-duplicate">
              <CopyIcon className="h-4 w-4 mr-2" />
              {user ? "Duplicate exactly" : "Sign up to use this lesson"}
            </Button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="max-w-4xl mx-auto">
        {artifactKeys.length > 0 && (
          <div className="tb-card p-2 mb-4">
            <div className="flex gap-1 overflow-x-auto tb-no-scrollbar">
              {artifactKeys.map((k) => {
                const meta = ARTIFACT_META[k] || { icon: Sparkles, label: k };
                const Icon = meta.icon;
                const active = tab === k;
                return (
                  <button
                    key={k}
                    onClick={() => setTab(k)}
                    className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition ${
                      active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                    data-testid={`public-tab-${k}`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Notes (if author opted in) */}
        {lesson.notes && (
          <div className="tb-card p-6 mb-4 tb-fade-up">
            <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground mb-3">Teacher notes</p>
            <Markdown className={subjectThemeClass(lesson.subject)}>{lesson.notes}</Markdown>
          </div>
        )}

        {/* Active artifact */}
        {tab && lesson.artifacts[tab] && (
          <div className="tb-card p-6 sm:p-8 tb-fade-up">
            <Markdown className={subjectThemeClass(lesson.subject)}>{lesson.artifacts[tab].content}</Markdown>
          </div>
        )}

        {/* Footer CTA */}
        <div className="tb-card p-6 sm:p-8 mt-6 text-center tb-card-feature bg-gradient-to-br from-primary/5 via-card to-primary/5">
          <div className="mx-auto h-12 w-12 rounded-full tb-ai-orb grid place-items-center mb-3 tb-float">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <h3 className="font-display text-xl tracking-tight font-medium">Build your own with TeeBee</h3>
          <p className="mt-1.5 text-sm text-muted-foreground max-w-md mx-auto">
            Turn any document, photo or topic into a complete lesson — quiz, worksheet, presentation and more.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            {user ? (
              <Button className="rounded-full" onClick={() => navigate("/teach-from-anything")}>
                <Wand2 className="h-4 w-4 mr-2" /> Teach from anything
              </Button>
            ) : (
              <>
                <Button className="rounded-full" onClick={() => navigate(`/signup?duplicate=${shareToken}`)} data-testid="public-cta-signup">
                  Sign up free <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                <Button variant="outline" className="rounded-full" onClick={() => navigate("/login")} data-testid="public-cta-login">
                  I have an account
                </Button>
              </>
            )}
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function PublicShell({ children }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <div className="min-h-screen tb-auth-bg">
      <header className="sticky top-0 z-30 tb-glass border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="public-logo">
            <Logo size="sm" to={null} />
          </button>
          <div className="flex items-center gap-2">
            {user ? (
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate("/dashboard")}>
                Dashboard
              </Button>
            ) : (
              <>
                <Button size="sm" variant="ghost" className="rounded-full hidden sm:inline-flex" onClick={() => navigate("/login")}>
                  Sign in
                </Button>
                <Button size="sm" className="rounded-full" onClick={() => navigate("/signup")}>
                  Try TeeBee free
                </Button>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">{children}</main>
    </div>
  );
}
